import { CheckIcon } from "@/components/appIcons";
import { cn } from "@/lib/utils";

import type { FilterOption } from "./filter-select";

interface FilterSelectOptionProps {
  clearable: boolean;
  isMobileSort: boolean;
  onChange: (value: string | null) => void;
  onClose: () => void;
  option: FilterOption;
  selectedValue: string | null;
}

function optionClassName(isMobileSort: boolean, isSelected: boolean): string {
  const base = "w-full flex items-center gap-2 text-left text-sm text-foreground transition-colors";

  if (isMobileSort) {
    return cn(
      base,
      "min-h-12 rounded-lg border px-3 py-3",
      isSelected
        ? "border-primary/50 bg-primary/10"
        : "border-border bg-secondary/30 hover:bg-secondary/80",
    );
  }

  return cn(base, "px-3 py-2.5", isSelected ? "bg-primary/10" : "hover:bg-secondary/80");
}

function SortSelectionIndicator({
  isMobileSort,
  isSelected,
}: {
  isMobileSort: boolean;
  isSelected: boolean;
}) {
  if (!isMobileSort) return null;

  return (
    <span
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-full border",
        isSelected ? "border-primary" : "border-muted-foreground/60",
      )}
      aria-hidden="true"
    >
      {isSelected ? <span className="size-2 rounded-full bg-primary" /> : null}
    </span>
  );
}

function OptionIcon({ option }: { option: FilterOption }) {
  if (option.iconUrl) {
    return <img src={option.iconUrl} alt="" className="w-4 h-auto rounded-sm shrink-0" />;
  }

  if (option.icon) {
    const Icon = option.icon;
    return <Icon className="text-[18px] text-muted-foreground shrink-0" />;
  }

  return null;
}

export function FilterSelectOption({
  clearable,
  isMobileSort,
  onChange,
  onClose,
  option,
  selectedValue,
}: FilterSelectOptionProps) {
  const isSelected = option.value === selectedValue;

  return (
    <button
      type="button"
      role={isMobileSort ? "radio" : undefined}
      aria-checked={isMobileSort ? isSelected : undefined}
      onClick={() => {
        if (!isSelected || clearable) {
          onChange(isSelected ? null : option.value);
        }
        onClose();
      }}
      className={optionClassName(isMobileSort, isSelected)}
    >
      <SortSelectionIndicator isMobileSort={isMobileSort} isSelected={isSelected} />
      <OptionIcon option={option} />
      <span className="flex-1 truncate">{option.label}</span>
      {option.count != null ? (
        <span
          className={cn(
            "text-xs tabular-nums px-1.5 py-0.5 rounded-full",
            isSelected ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground",
          )}
        >
          {option.count}
        </span>
      ) : null}
      {isSelected && !isMobileSort ? <CheckIcon className="text-primary text-[16px]" /> : null}
    </button>
  );
}
